import { useState, type ReactNode } from "react";
import { Search, ExternalLink, Menu, X } from "lucide-react";
import { Link, useRouter } from "../router/Router";
import { CATEGORIES, guidesByCategory, groupsForCategory } from "../content/registry";
import { CategoryIcon } from "./Icon";
import type { CategoryId } from "../content/types";

const APP_URL = "https://app.pulsepanels.com";

function Sidebar({ activePath }: { activePath: string }) {
  return (
    <nav className="hc-nav" aria-label="Help topics">
      {CATEGORIES.map((cat) => (
        <div key={cat.id} className="hc-nav__cat">
          <Link
            to={`/${cat.id}`}
            className="hc-nav__cat-head"
            style={{ ["--accent" as string]: cat.accent }}
          >
            <span className="hc-nav__cat-icon">
              <CategoryIcon name={cat.icon} size={16} />
            </span>
            {cat.label}
          </Link>
          <CategoryLinks id={cat.id} activePath={activePath} />
        </div>
      ))}
    </nav>
  );
}

function CategoryLinks({
  id,
  activePath,
}: {
  id: CategoryId;
  activePath: string;
}) {
  const guides = guidesByCategory(id);
  const groups = groupsForCategory(id);

  const renderLink = (route: string, title: string) => (
    <Link
      key={route}
      to={route}
      className={
        "hc-nav__link" + (activePath === route ? " hc-nav__link--active" : "")
      }
    >
      {title}
    </Link>
  );

  if (groups.length === 0) {
    return (
      <div className="hc-nav__links">
        {guides.map((g) => renderLink(g.route, g.title))}
      </div>
    );
  }

  // Grouped rendering: ungrouped guides first, then each labelled group.
  const ungrouped = guides.filter((g) => !g.group);
  return (
    <div className="hc-nav__links">
      {ungrouped.map((g) => renderLink(g.route, g.title))}
      {groups.map((grp) => (
        <div key={grp} className="hc-nav__group">
          <p className="hc-nav__group-label">{grp}</p>
          {guides
            .filter((g) => g.group === grp)
            .map((g) => renderLink(g.route, g.title))}
        </div>
      ))}
    </div>
  );
}

export function HelpLayout({ children }: { children: ReactNode }) {
  const { path, navigate } = useRouter();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = q.trim();
    if (query) navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="hc-root">
      <header className="hc-header">
        <div className="hc-header__inner">
          <button
            type="button"
            className="hc-header__menu"
            aria-label="Toggle topics"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>

          <Link to="/" className="hc-header__brand">
            <img
              src="/brand/01_primary_horizontal_logo_transparent.svg"
              alt="PulsePanels"
              className="hc-header__logo"
            />
            <span className="hc-header__tag">Help Center</span>
          </Link>

          <form className="hc-search" onSubmit={submitSearch} role="search">
            <Search size={16} className="hc-search__icon" aria-hidden="true" />
            <input
              className="hc-search__input"
              type="search"
              placeholder="Search guides…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search guides"
            />
          </form>

          <a className="hc-header__app" href={APP_URL} rel="noopener">
            Open dashboard
            <ExternalLink size={15} aria-hidden="true" />
          </a>
        </div>
      </header>

      <div className="hc-shell">
        <aside className={"hc-sidebar" + (open ? " hc-sidebar--open" : "")}>
          <Sidebar activePath={path} />
        </aside>

        {open ? (
          <button
            type="button"
            className="hc-scrim"
            aria-label="Close topics"
            onClick={() => setOpen(false)}
          />
        ) : null}

        <main className="hc-main">{children}</main>
      </div>

      <footer className="hc-footer">
        <div className="hc-footer__inner">
          <span>© {YEAR} PulsePanels</span>
          <span className="hc-footer__links">
            <a href={APP_URL} rel="noopener">Dashboard</a>
            <a href="https://pulsepanels.com" rel="noopener">pulsepanels.com</a>
            <a href="mailto:support@pulsepanels.com">Contact support</a>
          </span>
        </div>
      </footer>
    </div>
  );
}

const YEAR = 2026;
